import {useState} from 'react';
import {Plus} from 'lucide-react';
import {Button, FieldError, Input, Spinner} from '@kroll-resolver-crisp/ui-components-react';
import {splitIds} from './ExampleVideoPicker';
import {usePinterestLatestPins} from '@/api/hooks';
import {
  ExampleChip,
  ExampleGridLabel,
  ExampleTile,
  ExampleTileGrid
} from '@/components/classify/ExampleTile';
import {parsePinterestPinInput} from '@/lib/pinterest';

type Props = {
  readonly username: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
};

/** Pin counterpart of ExampleVideoPicker: pick from the profile's latest pins or paste a pin link/id. The value is newline-joined pin ids. */
export function ExamplePinPicker({username, value, onChange}: Props) {
  const ids = splitIds(value);
  const latest = usePinterestLatestPins(username);
  const [entry, setEntry] = useState('');
  const [error, setError] = useState<string | null>(null);

  const setIds = (next: string[]) => {
    onChange(next.join('\n'));
  };

  const toggle = (id: string) => {
    setIds(ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);
  };

  const addEntry = () => {
    const parts = splitIds(entry);
    const parsed = parts.map((part) => ({part, id: parsePinterestPinInput(part)}));
    const bad = parsed.find((p) => p.id === null);
    if (bad) {
      setError(`"${bad.part}" is not a pin ID or Pinterest pin link`);
      return;
    }

    setIds([...new Set([...ids, ...parsed.map((p) => p.id!)])]);
    setEntry('');
  };

  const pins = latest.data?.results ?? [];
  const pinById = new Map(pins.map((pin) => [pin.pin_id, pin]));

  return (
    <div className="space-y-3">
      {ids.length > 0 ? (
        <ul className="flex flex-wrap gap-2">
          {ids.map((id) => {
            const pin = pinById.get(id);
            return (
              <ExampleChip
                key={id}
                title={pin?.description ?? `Pin ${id}`}
                thumbnailUrl={pin?.thumbnail_url}
                thumbnailClassName="h-7 w-7"
                onRemove={() => {
                  toggle(id);
                }}
              />
            );
          })}
        </ul>
      ) : null}

      <div className="space-y-1">
        <div className="flex gap-2">
          <Input
            value={entry}
            placeholder="Paste a pin link or ID, then press Enter"
            aria-label="Add example pin by ID or link"
            aria-invalid={error ? true : undefined}
            onChange={(event) => {
              setEntry(event.target.value);
              setError(null);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                addEntry();
              }
            }}
          />
          <Button
            type="button"
            variant="secondary"
            disabled={entry.trim() === ''}
            onClick={addEntry}>
            <Plus />
            Add
          </Button>
        </div>
        {error ? <FieldError>{error}</FieldError> : null}
      </div>

      <div className="space-y-1.5">
        <ExampleGridLabel>Latest pins</ExampleGridLabel>
        {latest.isLoading ? (
          <div className="text-muted-foreground flex items-center gap-2 text-xs">
            <Spinner />
            Loading latest pins…
          </div>
        ) : latest.isError ? (
          <p className="text-muted-foreground text-xs">
            Couldn&apos;t load this profile&apos;s pins — you can still add links above.
          </p>
        ) : pins.length === 0 ? (
          <p className="text-muted-foreground text-xs">No pins found for this profile.</p>
        ) : (
          <ExampleTileGrid label="Latest pins">
            {pins.map((pin) => (
              <ExampleTile
                key={pin.pin_id}
                title={pin.description ?? `Pin ${pin.pin_id}`}
                thumbnailUrl={pin.thumbnail_url}
                selected={ids.includes(pin.pin_id)}
                aspect="square"
                onToggle={() => {
                  toggle(pin.pin_id);
                }}
              />
            ))}
          </ExampleTileGrid>
        )}
      </div>
    </div>
  );
}
